"use client";

import {
  createClient,
  fetchExchange,
  UrqlProvider as Provider,
  ssrExchange,
} from "@urql/next";
import { useMemo } from "react";
import {
  createOverCapacityErrorExchange,
  OverCapacityProvider,
  useOverCapacity,
} from "@/app/[lang]/_over-capacity";
import { getApiUrl } from "@/config";
import { useMetricWatchCacheGeneration } from "@/lib/metric-watch-refresh";
import { authExchange } from "./auth-exchange";
import { authRedirectExchange } from "./auth-redirect-exchange";
import { cacheExchange } from "./cache-exchange";
import { createClientContextExchange } from "./client-context-exchange";
import { demoModeExchange } from "./demo-mode-exchange";
import { createRetryExchange } from "./retry-exchange";
import { traceContextExchange } from "./trace-context-exchange";

/**
 * クライアント側で URQL クライアントを組み立て、共通の exchange 群を提供する内部プロバイダーです。
 *
 * @param children ラップ対象の要素。
 * @param token 認証トークン。
 */
export const UrqlProviderInner = ({
  children,
  token,
}: {
  children: React.ReactNode;
  token?: string;
}) => {
  const { showOverCapacityError } = useOverCapacity();
  const metricWatchCacheGeneration = useMetricWatchCacheGeneration();

  const [client, ssr] = useMemo(() => {
    // Server-side destructive mutations bump this token to force a new Graphcache instance.
    void metricWatchCacheGeneration;
    const ssr = ssrExchange({ isClient: typeof window !== "undefined" });

    const client = createClient({
      url: getApiUrl(),
      exchanges: [
        createClientContextExchange(token ?? undefined),
        authRedirectExchange,
        cacheExchange,
        ssr,
        createOverCapacityErrorExchange({
          onOverCapacityError: (errorType) => {
            showOverCapacityError(errorType);
          },
        }),
        authExchange,
        demoModeExchange,
        traceContextExchange,
        // transient な 5xx read error を bounded backoff で retry する（fetch の直前）。
        createRetryExchange(),
        fetchExchange,
      ],
    });
    return [client, ssr];
  }, [token, showOverCapacityError, metricWatchCacheGeneration]);

  return (
    <Provider client={client} ssr={ssr}>
      {children}
    </Provider>
  );
};

/**
 * OverCapacityProvider と URQL Provider をラップした外側のプロバイダーです。
 *
 * @param children ラップ対象の要素。
 * @param token 認証トークン。
 */
export const UrqlProvider = ({
  children,
  token,
}: {
  children: React.ReactNode;
  token?: string;
}) => {
  return (
    <OverCapacityProvider>
      <UrqlProviderInner token={token}>{children}</UrqlProviderInner>
    </OverCapacityProvider>
  );
};
